import { ExtractedFilterCondition } from 'Components/forms/DetectionFieldFiltersForm/constants';
import yaml from 'js-yaml';
import { AlertListingType, AlertType, AnalysisType, DetectionType } from 'Generated/schema';
import { capitalize } from 'Helpers/utils';
import PypantherIcon from 'Assets/ruleTypes/pypanther.svg';
import PythonIcon from 'Assets/python-logo.svg';
import CorrelationIcon from 'Assets/ruleTypes/correlation.svg';
import PolicyIcon from 'Assets/policy.svg';
import ScheduledRuleIcon from 'Assets/ruleTypes/scheduled.svg';
import PantherLogo from 'Assets/panther-minimal-logo.svg';
import PantherLogoLight from 'Assets/panther-minimal-logo-light.svg';
import { RuleSummary } from 'Source/graphql/fragments/RuleSummary.generated';
import { PolicyDetails } from 'Source/graphql/fragments/PolicyDetails.generated';
import { RuleDetails } from 'Source/graphql/fragments/RuleDetails.generated';

export const DEDUP_PERIOD_MINUTES_OPTIONS = [15, 30, 60, 180, 720, 1440];

export const ALERT_MATCH_TYPES = [
  AlertType.Rule,
  AlertType.ScheduledRule,
  AlertType.CorrelationRule,
];

export const ALERT_ERROR_TYPES = [
  AlertType.RuleError,
  AlertType.ScheduledRuleError,
  AlertType.CorrelationRuleError,
];

const AlertTypesByListingType: Record<AlertListingType, AlertType[]> = {
  [AlertListingType.Alert]: ALERT_MATCH_TYPES,
  [AlertListingType.DetectionError]: ALERT_ERROR_TYPES,
  [AlertListingType.SystemError]: [],
};

export const getAlertSubTypesByListingType = (type: AlertListingType) => {
  return AlertTypesByListingType[type] ?? [];
};

/**
 * Translating JSON from simple detection creation to Detections Serialization Format (DSF)
 *
 * Detection logic comes in this format from the Simple Detections Creation
 * { conjunction: 'and',
 *   values: [
 *     {target: '', operator: '', value: ''},
 *     {conjunction: 'or', values: [
 *       {target: '', operator: '', value: ''},
 *       {target: '', operator: '', value: ''}
 *     ]}
 *   ]}
 * And is changed to this JSON format
 * { All: [
 *     {KeyPath: '', Condition: '', Value: ''},
 *     {Any: [
 *       {KeyPath: '', Condition: '', Value: ''},
 *       {KeyPath: '', Condition: '', Value: ''}
 *     ]}
 *   ]}
 *   So then yaml.dump has the right values
 */
export type FiltersWithJoins = {
  conjunction: string;
  values: (FiltersWithJoins | ExtractedFilterCondition)[];
  // Set only when the filter can't be represented in the rule builder, forcing the YAML view.
  ruleBuilderCompatible?: boolean;
};

enum JsonYamlConjunctions {
  and = 'All',
  or = 'Any',
}

enum JsonYamlOperators {
  in = 'IsIn',
  '!in' = 'IsNotIn',
  empty = 'IsNullOrEmpty',
  '!empty' = 'IsNotNullOrEmpty',
  '==' = 'Equals',
  '!=' = 'DoesNotEqual',
  'starts_with' = 'StartsWith',
  '!starts_with' = 'DoesNotStartWith',
  'ends_with' = 'EndsWith',
  '!ends_with' = 'DoesNotEndWith',
  contains = 'Contains',
  '!contains' = 'DoesNotContain',
  'is_private' = 'IsIPAddressPrivate',
  'is_public' = 'IsIPAddressPublic',
  'in_cidr' = 'IsIPAddressInCIDR',
  '!in_cidr' = 'IsIPAddressNotInCIDR',
  'contains_ip' = 'CIDRContainsIPAddresses',
  '!contains_ip' = 'CIDRDoesNotContainIPAddresses',
  '>' = 'IsGreaterThan',
  '>=' = 'IsGreaterThanOrEquals',
  '<' = 'IsLessThan',
  '<=' = 'IsLessThanOrEquals',
  'insensitive_equals' = 'IEquals',
  '!insensitive_equals' = 'IDoesNotEqual',
  'insensitive_starts_with' = 'IStartsWith',
  '!insensitive_starts_with' = 'IDoesNotStartWith',
  'insensitive_ends_with' = 'IEndsWith',
  '!insensitive_ends_with' = 'IDoesNotEndWith',
  'insensitive_contains' = 'IContains',
  '!insensitive_contains' = 'IDoesNotContain',
}
export const formatFiltersAsDSF = (jsonFilters: FiltersWithJoins) => {
  const isCompleteFilterCondition = (
    filter: FiltersWithJoins | ExtractedFilterCondition,
  ): boolean => {
    const asCondition = filter as ExtractedFilterCondition;
    if ('target' in asCondition && 'operator' in asCondition) {
      return !!asCondition.target && !!asCondition.operator;
    }
    return true;
  };

  const formatAsJsonForDSF = (filter: FiltersWithJoins | ExtractedFilterCondition) => {
    if ((filter as ExtractedFilterCondition)?.target) {
      const statement = filter as ExtractedFilterCondition;
      return {
        KeyPath: statement.target,
        Condition: JsonYamlOperators[statement.operator],
        ...('value' in statement
          ? { [`Value${Array.isArray(statement.value) ? 's' : ''}`]: statement.value }
          : {}),
      };
    }
    const conjunction = (filter as FiltersWithJoins)?.conjunction || 'and';
    const values = ((filter as FiltersWithJoins)?.values || []).filter(isCompleteFilterCondition);
    return {
      [JsonYamlConjunctions[conjunction]]: values.map(value => formatAsJsonForDSF(value)),
    };
  };

  const formattedDSF = yaml.dump(formatAsJsonForDSF(jsonFilters));
  return `- ${formattedDSF}`;
};

export const findMaximumDepth = (json: FiltersWithJoins, startDepth = 0) =>
  json.values.reduce(
    (depth, condition) =>
      (condition as FiltersWithJoins).values
        ? Math.max(findMaximumDepth(condition as FiltersWithJoins, startDepth + 1), depth)
        : depth,
    startDepth,
  );

/**
 *
 * Translating simple detection creation from Detections Serialization Format (DSF) to JSON
 *
 * yaml.load parses the DSF into this JSON format
 * { All: [
 *     {KeyPath: '', Condition: '', Value: ''},
 *     {Any: [
 *       {KeyPath: '', Condition: '', Value: ''},
 *       {KeyPath: '', Condition: '', Value: ''}
 *     ]}
 *   ]}
 * This function translates into this format for the simple detection builder
 * { conjunction: 'and',
 *   values: [
 *     {target: '', operator: '', value: ''},
 *     {conjunction: 'or', values: [
 *       {target: '', operator: '', value: ''},
 *       {target: '', operator: '', value: ''}
 *     ]}
 *   ]}
 *
 */
enum DSFJsonConjunctions {
  'All' = 'and',
  'Any' = 'or',
}

enum DSFJsonOperators {
  'IsIn' = 'in',
  'IsNotIn' = '!in',
  'IsNullOrEmpty' = 'empty',
  'IsNotNullOrEmpty' = '!empty',
  'Equals' = '==',
  'DoesNotEqual' = '!=',
  'StartsWith' = 'starts_with',
  'DoesNotStartWith' = '!starts_with',
  'EndsWith' = 'ends_with',
  'DoesNotEndWith' = '!ends_with',
  'Contains' = 'contains',
  'DoesNotContain' = '!contains',
  'IsIPAddressPrivate' = 'is_private',
  'IsIPAddressPublic' = 'is_public',
  'IsIPAddressInCIDR' = 'in_cidr',
  'IsIPAddressNotInCIDR' = '!in_cidr',
  'CIDRContainsIPAddresses' = 'contains_ip',
  'CIDRDoesNotContainIPAddresses' = '!contains_ip',
  'IsGreaterThan' = '>',
  'IsGreaterThanOrEqual' = '>=',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias
  'IsGreaterThanOrEquals' = '>=',
  'IsLessThan' = '<',
  'IsLessThanOrEqual' = '<=',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias
  'IsLessThanOrEquals' = '<=',
  'IEquals' = 'insensitive_equals',
  'IDoesNotEqual' = '!insensitive_equals',
  'IStartsWith' = 'insensitive_starts_with',
  'IDoesNotStartWith' = '!insensitive_starts_with',
  'IEndsWith' = 'insensitive_ends_with',
  'IDoesNotEndWith' = '!insensitive_ends_with',
  'IContains' = 'insensitive_contains',
  'IDoesNotContain' = '!insensitive_contains',
}

const FILTER_INCOMPATIBLE_WITH_RULE_BUILDER =
  'This filter contains legal YAML that cannot be represented in the Rule Builder';

function formatAsJsonForFilters(filter) {
  if (filter.KeyPath && filter.Condition) {
    const value = filter.Value ?? filter.Values;
    // we must handle falsy values that the user explicitly entered here, otherwise
    // we omit the value entirely. the only cases we care about are bools and numbers because
    // user can explicitly say value == false or value == 0.
    // but for other types like strings/arrays we dont handle the falsy case (empty string/empty array)
    // because we have explicit operators for those "isEmpty"
    const isFalsyBool = typeof value === 'boolean' && !value;
    const isZero = typeof value === 'number' && value === 0;
    const hasValue = isFalsyBool || isZero || value;
    return {
      target: filter.KeyPath,
      operator: DSFJsonOperators[filter.Condition],
      ...(hasValue && { value }),
    } as ExtractedFilterCondition;
  }

  if (
    DSFJsonConjunctions[Object.keys(filter)[0]] &&
    Array.isArray(filter[Object.keys(filter)[0]])
  ) {
    return {
      conjunction: DSFJsonConjunctions[Object.keys(filter)[0]],
      values: filter[Object.keys(filter)[0]].map(value => formatAsJsonForFilters(value)),
    } as FiltersWithJoins;
  }

  if (filter.KeyPath) {
    return {
      target: filter.KeyPath,
      operator: '',
    } as ExtractedFilterCondition;
  }

  throw new Error(FILTER_INCOMPATIBLE_WITH_RULE_BUILDER);
}

export const formatDSFAsFilters = (dsf: string) => {
  const parsedDsf = yaml.load(dsf);

  if (!parsedDsf || !parsedDsf[0]) {
    return {
      conjunction: 'and',
      values: [],
    } as FiltersWithJoins;
  }

  /**
   Checks whether the filters already have a wrapper `All` or `Any` around
   them. If the parsed JSON is length of 1 and is wrapped in a conjunction,
   then we don't need to add another `All`

   Needs a wrapper: [{All: [{Keypath: ...}], {Any: [{Keypath: ...}]}]
   Needs a wrapper: [{Keypath: ...}]
   Does not need a wrapper: [{Any: [{Keypath: ...}]}]
   Does not need a wrapper: [{All: [{Keypath: ...}]}]
   */
  const formattedParsedDsf =
    (parsedDsf as any[]).length === 1 && DSFJsonConjunctions[Object.keys(parsedDsf[0])[0]]
      ? parsedDsf
      : [{ All: parsedDsf }];

  try {
    return formatAsJsonForFilters(formattedParsedDsf[0]) as FiltersWithJoins;
  } catch (e) {
    if (e.message === FILTER_INCOMPATIBLE_WITH_RULE_BUILDER) {
      return {
        conjunction: 'and',
        values: [],
        ruleBuilderCompatible: false,
      } as FiltersWithJoins;
    }
    throw e;
  }
};

const ANALYSIS_TYPE_LABELS: Record<string, string> = {
  [DetectionType.Rule]: 'Rule',
  [DetectionType.Policy]: 'Policy',
  [DetectionType.ScheduledRule]: 'Scheduled Rule',
  [DetectionType.CorrelationRule]: 'Correlation Rule',
  [AnalysisType.SavedQuery]: 'Saved Query',
  [AnalysisType.ScheduledQuery]: 'Scheduled Query',
  [AnalysisType.GlobalHelper]: 'Global Helper',
  [AnalysisType.DataModel]: 'Data Model',
  [AnalysisType.Enrichment]: 'Enrichment',
  [`${DetectionType.Rule}s`]: 'Rules',
  [`${DetectionType.Policy}s`]: 'Policies',
  [`${DetectionType.ScheduledRule}s`]: 'Scheduled Rules',
  [`${DetectionType.CorrelationRule}s`]: 'Correlation Rules',
  [`${AnalysisType.SavedQuery}s`]: 'Saved Queries',
  [`${AnalysisType.ScheduledQuery}s`]: 'Scheduled Queries',
  [`${AnalysisType.GlobalHelper}s`]: 'Global Helpers',
  [`${AnalysisType.DataModel}s`]: 'Data Models',
  [`${AnalysisType.Enrichment}s`]: 'Enrichments',
};

export const analysisTypeToLabel = (type: DetectionType | AnalysisType, plural = false): string => {
  const key = type.toUpperCase();
  return ANALYSIS_TYPE_LABELS[plural ? `${key}s` : key] ?? type;
};

export const severityItemToString = (severity: string) => capitalize(severity.toLowerCase());

export interface DetectionIconInfo {
  icon: string | null;
  type: string | null;
}

export function getDetectionIcon(
  detection: Partial<PolicyDetails | RuleDetails>,
  lightMode?: boolean,
): DetectionIconInfo {
  const { analysisType } = detection || {};

  switch (analysisType) {
    case DetectionType.Rule:
    case DetectionType.DerivedRule: {
      const specVersion = (detection as RuleSummary)?.specVersion;
      const isSimpleDetection = (detection as RuleSummary)?.isSimpleDetection;

      let ruleIcon: string;
      let ruleType: string;

      if (isSimpleDetection) {
        ruleIcon = lightMode ? PantherLogoLight : PantherLogo;
        ruleType = 'Detection Builder Rule';
      } else if (specVersion === 'v2') {
        ruleIcon = PypantherIcon;
        ruleType = 'PyPanther Rule';
      } else {
        ruleIcon = PythonIcon;
        ruleType = 'Python Rule';
      }
      return {
        icon: ruleIcon,
        type: ruleType,
      };
    }
    case DetectionType.CorrelationRule:
      return {
        icon: CorrelationIcon,
        type: 'Correlation Rule',
      };
    case DetectionType.Policy:
      return {
        icon: PolicyIcon,
        type: 'Policy',
      };
    case DetectionType.ScheduledRule:
      return {
        icon: ScheduledRuleIcon,
        type: 'Scheduled Rule',
      };
    default:
      // Fallback for unknown types
      return {
        icon: null,
        type: null,
      };
  }
}
